import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useMe, useSaveLanguage } from "../api/queries";
import { LANGUAGES, type Lang } from ".";

/** The active language, narrowed to what we actually ship. */
export function useLanguage(): Lang {
  const { i18n } = useTranslation();
  const resolved = i18n.resolvedLanguage ?? "";
  return (LANGUAGES as readonly string[]).includes(resolved) ? (resolved as Lang) : "en";
}

/** Change it: i18next (which caches to localStorage) and, when signed in, the account. */
export function useSetLanguage(): (lang: Lang) => void {
  const { i18n } = useTranslation();
  const { data: me } = useMe();
  const save = useSaveLanguage();
  return (lang: Lang) => {
    void i18n.changeLanguage(lang);
    if (me) save.mutate(lang);
  };
}

/** Adopt the account's language once, when a user resolves. Called once, above the development gate. */
export function useLanguageSync(): void {
  const { i18n } = useTranslation();
  const { data: me } = useMe();
  // Guarded on the account id and not on the value: a signed-in user who switches language
  // would otherwise have the next `me` refetch snap it straight back.
  const applied = useRef<number | null>(null);
  useEffect(() => {
    if (!me) {
      // Signed out. The active language stays as it is -- the visitor just used the UI in it.
      applied.current = null;
      return;
    }
    if (applied.current === me.id) return;
    applied.current = me.id;
    if (me.language !== i18n.resolvedLanguage) void i18n.changeLanguage(me.language);
  }, [me, i18n]);
}
